import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box } from '@material-ui/core';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { User } from 'component/stylist_profile_header/types';
import { useAuth } from 'contexts/AuthContext';
import { GET_USER_BY_USERNAME } from 'graph_ql/users/queries';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setSkinToneDefault } from 'redux/authentication/user_detail';
import { EQuestionId, EQuizId, ESubjectQuestion, quizContextType } from 'types/quiz';
import { getValueDescription } from 'utils/utils';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { useGetGender } from '../../../hooks/useGetGender';
import { PATH } from '../../../routes/constant';
import { useStyles } from './styles';
import EmptyAnswer from 'component/StandardAlert/no_answer';
import { SkinToneItem } from './skin_tone_item';
import ScrollContainer from 'react-indiana-drag-scroll';
import useValidateAnswers from '../hooks/useValidateAnswers';
import { QuizSpinner } from 'component/layout_quiz/quiz-spinner';
export interface ISkinToneProps {}

const SkinTone = (props: { hideTitle?: boolean }) => {
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const history = useHistory();
    const gender = useGetGender(Boolean(userName));
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const listRef = useRef<HTMLDivElement>(null);
    const [requiredQuestion, setRequiredQuestion] = useState(true);
    const [user, setUser] = useState<User>();
    const [defaultValueSkinTone, setDefaultValueSkinTone] = useState<any>(null);
    const [quizData, setQuizData] = useState<any>(null);
    const [loadingSkip, setLoadingSkip] = useState<any>(false);
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);

    const [getQuizResponse, { loading: loadingQuiz }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            setRequiredQuestion(data.getQuizResponse?.questionsInfo[0]?.required);
            setDefaultValueSkinTone(data.getQuizResponse?.answers?.skintone_question);
            setQuizData(data.getQuizResponse);
        },
    });
    const [getUserByUserName, { loading: userLoading }] = useLazyQuery(GET_USER_BY_USERNAME, {
        onCompleted(data) {
            setUser(data.getUserByUserName);
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.SKIN_TONE,
                        contextType: quizContextType,
                        contextId: EQuizId.SKIN_TONE,
                        userId: data.getUserByUserName?.id,
                    },
                },
            });
        },
    });
    const createAnswer = async (input: {
        questionId: String;
        questionSubject: String;
        contextType: String;
        contextId: String;
        value: String;
        valueType: String;
        isSkipped?: boolean;
        id?: String;
    }) => {
        const variables = {
            input,
        };
        await answerMutation({ variables });
    };
    const [answerMutation, { loading: loadingAnswer }] = useMutation(CREATE_ANSWER, {
        onCompleted(response) {
            onAnswerCompleted(response);
            dispatch(setSkinToneDefault(response.createAnswer));
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const onAnswerCompleted = async (response: any) => {
        await getQuizResponse({
            variables: {
                input: {
                    quizId: EQuizId.SKIN_TONE,
                    contextType: quizContextType,
                    contextId: EQuizId.SKIN_TONE,
                    userId: currentUser.uid || gender?.id,
                },
            },
        });
        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    };
    const { data: skinToneData, loading } = useQuery(GET_QUESTION_DATA, {
        variables: {
            input: {
                subject: ESubjectQuestion.SKIN_TONE,
                paging: { page: 0, size: 0 },
                enabled: true,
            },
        },
        skip: Boolean(!userName) ? !gender : !user,
    });

    const listSkinTones = useMemo(
        () => (skinToneData?.getQuestionData?.data || []).map(getValueDescription),
        [skinToneData?.getQuestionData?.data],
    );
    const [listSkinToneOptions, setListSkinToneOptions] = useState(listSkinTones || []);

    const isValidAnswer = useValidateAnswers(
        isUpdate,
        listSkinToneOptions.find((item) => item.isSelect)?.value,
        answerAlreadyUpdated,
    );

    useEffect(() => {
        if ((currentUser && Boolean(!userName)) || gender) {
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.SKIN_TONE,
                        contextType: quizContextType,
                        contextId: EQuizId.SKIN_TONE,
                        userId: currentUser.uid || gender?.id,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.uid, gender, userName]);
    useEffect(() => {
        if (userName) {
            getUserByUserName({
                variables: {
                    input: {
                        userName,
                    },
                },
            });
        }
    }, [userName, getUserByUserName]);
    useEffect(() => {
        let listSkinTonesCurrent: any = [];
        if (listSkinTones && listSkinTones.length > 0 && !loadingQuiz) {
            listSkinTonesCurrent = [...listSkinTones];
            const defaultValueIndex = listSkinTonesCurrent.findIndex(
                (item) => item.value.toLowerCase() === defaultValueSkinTone?.value?.toLowerCase(),
            );
            if (defaultValueIndex > 0) listSkinTonesCurrent[defaultValueIndex].isSelect = true;
            setListSkinToneOptions(listSkinTonesCurrent);
        }
    }, [defaultValueSkinTone, defaultValueSkinTone?.value, listSkinTones, loadingQuiz]);

    const handleSelectItem = (item) => {
        setAnswerUpdated(true);
        if (Boolean(!userName)) {
            setListSkinToneOptions(
                listSkinTones.map((d) => {
                    if (item.id === d.id) {
                        d.isSelect = true;
                    } else {
                        d.isSelect = false;
                    }
                    return d;
                }),
            );
        }
    };

    const input = {
        questionId: EQuestionId.SKIN_TONE,
        questionSubject: ESubjectQuestion.SKIN_TONE,
        contextType: quizContextType,
        contextId: EQuizId.SKIN_TONE,
        valueType: 'STRING',
        id: defaultValueSkinTone?.id,
    };
    const handleClickAnswer = () => {
        const currentItemSelected = listSkinToneOptions.find((item) => item.isSelect);
        if (Boolean(!userName) && currentItemSelected) {
            createAnswer({ ...input, value: currentItemSelected.value });
        }
    };
    const handleSkip = async () => {
        if (Boolean(!userName)) {
            setLoadingSkip(true);
            await createAnswer({ ...input, value: '', isSkipped: true });
            setLoadingSkip(false);
        }
    };

    const valueSkinTone = useMemo(() => listSkinToneOptions?.find((d) => d.isSelect), [listSkinToneOptions]);

    return (
        <CustomizeLayoutQuiz
            quizId={EQuizId.SKIN_TONE}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={!props.hideTitle && quizData?.title}
            handleAnswer={handleClickAnswer}
            isUpdate={location.pathname.includes(PATH.MY_STYLE_PREFERENCES)}
            isDisplayContent={Boolean(!userName)}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={requiredQuestion}
            handleSkipQuestion={handleSkip}
            loadingSkip={loadingAnswer && loadingSkip}
            loading={loadingAnswer && !loadingSkip}
            nextBtnDisabled={!isValidAnswer}
        >
            {(loading || loadingQuiz || userLoading) && <QuizSpinner />}
            <Box display={'flex'} flexDirection={'column'} textAlign={'center'} width={'100%'} height={'100%'}>
                <Box className={classes.quizRowContainer}>
                    {Boolean(!user) ? (
                        <ScrollContainer
                            className={classes.quizRow}
                            innerRef={listRef}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexWrap: 'wrap',
                                paddingLeft: '16px',
                                gap: 12,
                            }}
                        >
                            {listSkinToneOptions.length > 0 &&
                                !loading &&
                                listSkinToneOptions?.map((item, index) => (
                                    <SkinToneItem item={item} index={index} handleSelectItem={handleSelectItem} />
                                ))}
                        </ScrollContainer>
                    ) : valueSkinTone ? (
                        <Box width={'90%'} className={classes.wrapperItem} margin="0 auto">
                            <Box
                                className={classes.colorItem}
                                style={{
                                    backgroundColor: `${valueSkinTone?.bgColor}`,
                                    border: `3px solid`,
                                    height: 150,
                                }}
                            >
                                <p className={classes.itemTitle} style={{ color: `${valueSkinTone.color}` }}>
                                    {valueSkinTone?.value || ''}
                                </p>
                            </Box>
                        </Box>
                    ) : (
                        <EmptyAnswer title={'Skin Tone'} userName={user?.firstName} />
                    )}
                </Box>
            </Box>
        </CustomizeLayoutQuiz>
    );
};
export default SkinTone;
